import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { PROOF_SEQUENCE, ProofState, createProof, proofBadge } from '@cortis/proof-lifecycle';

import { Api, subscribeToRun } from '../lib/api.js';
import { useApi, useWallet } from '../lib/wallet.jsx';
import { Badge, ErrorState, Hash, Loading, Skeleton } from '../components/ui.jsx';
import { ProofPanel } from '../components/ProofPanel.jsx';
import { Markdown } from '../components/Markdown.jsx';

/**
 * One-on-one agent chat. The central surface (§11.1).
 *
 * Two things about this screen carry the product's honesty. The answer appears
 * as soon as the run reaches a terminal outcome, without waiting for the chain
 * — but it is labelled Proof pending until a receipt exists. And every message
 * is a separate interaction, so it gets its own run, its own evidence root and
 * its own attestation; there is no "continue" that mutates a prior run.
 *
 * The proof state shown here comes from the server through SSE. The browser
 * never advances it locally, and there is no timer anywhere in this file.
 */
export function AgentChat({ pushToast }) {
  const { agentId } = useParams();
  const location = useLocation();
  const initialTask = location.state?.initialTask ?? null;
  const { sessionReady } = useWallet();
  const { data: agent, loading, error, reload } = useApi(() => Api.agent(agentId), [agentId, sessionReady], { skip: !sessionReady });
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  // The agent's SOUL/brief (a.role) is shown expanded on arrival, then folds
  // away once the conversation starts so the chat itself owns the screen. It
  // stays available behind a toggle at all times.
  const [briefOpen, setBriefOpen] = useState(true);
  const collapsedOnce = useRef(false);
  const scrollRef = useRef(null);
  const unsubscribes = useRef([]);

  useEffect(() => {
    if (!collapsedOnce.current && messages.length > 0) {
      collapsedOnce.current = true;
      setBriefOpen(false);
    }
  }, [messages.length]);

  useEffect(() => () => unsubscribes.current.forEach((fn) => fn()), []);
  // Auto-scroll only when the user is already parked at the bottom. While a run
  // streams, proof.state updates land ~1/sec; force-scrolling on every update
  // would yank the view down and make scrolling up impossible. If the user has
  // scrolled up to read, we leave them there.
  const atBottomRef = useRef(true);
  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);
  useEffect(() => {
    if (!atBottomRef.current) return;
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  // A task handed over from Home runs once, automatically, so a first-time user
  // sees their request execute without having to find and press Send. The ref
  // guard makes it fire a single time even under StrictMode double-invocation.
  const autoRan = useRef(false);
  useEffect(() => {
    if (autoRan.current || !initialTask || !agent) return;
    autoRan.current = true;
    sendMessage(initialTask);
    // Clear the router state so a refresh does not resubmit the same task.
    window.history.replaceState({}, document.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent, initialTask]);

  const watchRun = useCallback((runId) => {
    const stop = subscribeToRun(runId, async (type, payload) => {
      if (type === 'proof.state' || type === 'run.terminal') {
        setMessages((prev) =>
          prev.map((m) => (m.runId === runId ? { ...m, proofState: payload.proofState, outcome: payload.outcome ?? m.outcome } : m)),
        );
      }
      if (type === 'run.terminal') {
        const proof = await Api.proof(runId).catch(() => null);
        setMessages((prev) => prev.map((m) => (m.runId === runId ? { ...m, proof } : m)));
      }
    });
    unsubscribes.current.push(stop);
  }, []);

  const sendMessage = async (explicit) => {
    const text = (typeof explicit === 'string' ? explicit : draft).trim();
    if (!text || sending) return;
    setSending(true);
    setDraft('');

    // A stable key per submission, so a network retry converges on one run
    // rather than executing the agent twice.
    const idempotencyKey = `run-${crypto.randomUUID()}`;
    setMessages((prev) => [...prev, { id: idempotencyKey, role: 'owner', text }]);

    try {
      const created = await Api.createRun(agentId, text, idempotencyKey);
      setMessages((prev) => [
        ...prev,
        { id: `${idempotencyKey}-a`, role: 'agent', runId: created.runId, proofState: created.proofState ?? ProofState.QUEUED },
      ]);
      watchRun(created.runId);
    } catch (err) {
      pushToast({ tone: 'danger', message: err.message });
      setMessages((prev) => [...prev, { id: `${idempotencyKey}-e`, role: 'system', text: err.message, error: true }]);
    } finally {
      setSending(false);
    }
  };

  if (error) return <ErrorState error={error} onRetry={reload} context="This agent could not be loaded" />;
  if (!sessionReady || loading || !agent) return <Skeleton rows={6} />;

  const a = agent.agent;

  return (
    <div className="chat">
      <header className="chat__header">
        <div className="chat__heading">
          <h1 className="page-title">{a.name}</h1>
          {a.role && (
            <details
              className="chat__brief"
              open={briefOpen}
              onToggle={(e) => setBriefOpen(e.currentTarget.open)}
            >
              <summary className="chat__brief-summary">
                <span className="chat__brief-caret" aria-hidden="true" />
                <span className="chat__brief-label">Agent overview</span>
              </summary>
              <div className="chat__brief-body">
                <Markdown text={a.role} />
              </div>
            </details>
          )}
        </div>
      </header>

      <div className="chat__scroll" ref={scrollRef} onScroll={onScroll}>
        {messages.length === 0 && (
          <div className="chat__empty">
            <p className="chat__empty-title">Ask anything</p>
            <p className="chat__empty-body">
              Type a task below. Your agent searches the web, reads what it finds, and writes you a clear answer with
              sources.
            </p>
          </div>
        )}

        {messages.map((m) =>
          m.role === 'owner' ? (
            <article key={m.id} className="msg msg--owner">
              <p className="msg__text">{m.text}</p>
            </article>
          ) : m.role === 'system' ? (
            <article key={m.id} className="msg msg--system" role="alert">
              <p className="msg__text">{m.text}</p>
            </article>
          ) : (
            <AgentTurn key={m.id} message={m} />
          ),
        )}
      </div>

      <div className="composer">
        <label className="sr-only" htmlFor="composer">Message this agent</label>
        <textarea
          id="composer"
          className="composer__input"
          rows={2}
          value={draft}
          placeholder="Message your agent"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // ChatGPT/Claude-style: Enter sends, Shift+Enter (or Cmd/Ctrl+Enter)
            // inserts a newline.
            if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey && !e.nativeEvent?.isComposing) {
              e.preventDefault();
              sendMessage();
            }
          }}
        />
        <button
          type="button"
          className="composer__send"
          onClick={() => sendMessage()}
          disabled={!draft.trim() || sending}
          aria-label="Send message"
          title="Send"
        >
          {sending ? (
            <span className="composer__send-spinner" aria-hidden="true" />
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 20V5M12 5l-6 6M12 5l6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}

/**
 * One agent turn: a friendly live progress feed while it works, then the
 * answer. The chain/proof detail is tucked into a subtle footnote so a
 * non-technical user is never confronted with it.
 */
function AgentTurn({ message }) {
  const state = message.proofState ?? ProofState.QUEUED;
  const badge = proofBadge(state);
  const index = PROOF_SEQUENCE.indexOf(state);
  const proof = message.proof;

  // Friendly, plain-language stages mapped from the proof lifecycle. The user
  // sees what the agent is doing, not the internal state names. These reflect
  // the real pipeline order (plan -> search+read -> write), so the feed is
  // honest, not a decorative animation.
  const STAGES = [
    { key: ProofState.QUEUED, label: 'Understanding your request', hint: 'Working out what you need' },
    { key: ProofState.EXECUTING, label: 'Thinking', hint: 'Reasoning and gathering anything needed' },
    { key: ProofState.EVIDENCE_FINALIZING, label: 'Writing your answer', hint: 'Composing a clear response' },
  ];
  const activeStage = Math.max(
    0,
    STAGES.findIndex((st) => st.key === state),
  );

  // Real elapsed time since the turn began. This is genuine wall-clock time,
  // not a fabricated progress bar.
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (proof) return undefined;
    const started = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 250);
    return () => clearInterval(id);
  }, [proof]);

  return (
    <article className="msg msg--agent">
      {!proof && (
        <div className="worklog">
          <div className="worklog__head">
            <span className="worklog__spinner" aria-hidden="true" />
            <span className="worklog__working">Working</span>
            <span className="worklog__timer">{elapsed}s</span>
          </div>
          {STAGES.map((st, i) => {
            const status = i < activeStage ? 'done' : i === activeStage ? 'active' : 'todo';
            return (
              <div key={st.key} className={`worklog__step worklog__step--${status}`}>
                <span className="worklog__dot" aria-hidden="true" />
                <span className="worklog__body">
                  <span className="worklog__label">{st.label}</span>
                  {status === 'active' && <span className="worklog__hint">{st.hint}</span>}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {proof && (
        <>
          <div className="msg__result">
            {proof.deliverableSummary ? (
              <Markdown className="msg__md" text={proof.deliverableSummary} />
            ) : (
              <p className="msg__text">This one finished but didn't return any text. Try asking again.</p>
            )}
          </div>
          <details className="msg__proof">
            <summary>
              <span className="msg__proof-toggle">Verified answer</span>
              <Badge tone={badge.tone}>{badge.label}</Badge>
            </summary>
            <p className="msg__proof-note">
              Every answer is logged with a tamper-evident record of what the agent did and which sources it used.
            </p>
            <ProofPanel
              proof={{
                ...createProof({ runId: proof.runId, runIdHash: proof.runIdHash }),
                state: proof.proofState,
                txHash: proof.attestation?.txHash ?? null,
                confirmations: proof.confirmed ? 3 : 0,
                indexed: proof.confirmed,
              }}
              deliverable={null}
              onRetry={() => {}}
            />
            <dl className="facts">
              <div className="facts__row">
                <dt className="facts__label">Record ID</dt>
                <dd className="facts__value"><Hash value={proof.runIdHash} label="record id" /></dd>
              </div>
              <div className="facts__row">
                <dt className="facts__label">Evidence root</dt>
                <dd className="facts__value"><Hash value={proof.evidenceRoot} label="evidence root" /></dd>
              </div>
              {proof.attestation && (
                <div className="facts__row">
                  <dt className="facts__label">Transaction</dt>
                  <dd className="facts__value"><Hash value={proof.attestation.txHash} label="transaction" /></dd>
                </div>
              )}
            </dl>
            {proof.attestation?.explorerUrl && (
              <a className="link" href={proof.attestation.explorerUrl} target="_blank" rel="noreferrer">
                View on the explorer
              </a>
            )}
          </details>
        </>
      )}
    </article>
  );
}
